import { UnknownAction, configureStore, ThunkAction, StoreEnhancer } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch as reduxUseDispatch, useSelector as reduxUseSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import config from 'config';

import { devicesDeviceUpdate } from 'modules/devices/slice';
import { linksLinksAddOrUpdate } from 'modules/links/slice';
import { devicesMiddleware } from 'modules/devices/middleware';
import { linksMiddleware } from 'modules/links/middleware';
import { pricesMiddleware } from 'modules/prices/middleware';
import { rootReducer, RootState } from 'app/store/reducer';

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(devicesMiddleware, linksMiddleware, pricesMiddleware),
  enhancers: (getDefaultEnhancers) => {
    if (!config.USE_SENTRY) {
      return getDefaultEnhancers();
    }

    const ignoredActions = [devicesDeviceUpdate.toString(), linksLinksAddOrUpdate.toString()];
    const sentryReduxEnhancer = Sentry.createReduxEnhancer({
      attachReduxState: false,
      actionTransformer: (action) => {
        if (ignoredActions.includes(action.type as string)) {
          return null;
        }

        return action;
      },
    }) as StoreEnhancer;

    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  },
});

if (import.meta.env.DEV) {
  (window as Window & { __wifiCalculatorStore?: typeof store }).__wifiCalculatorStore = store;
}

export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, UnknownAction>;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useDispatch: () => AppDispatch = reduxUseDispatch;
export const useSelector: TypedUseSelectorHook<RootState> = reduxUseSelector;
